import { useEffect, useState } from "react";

export interface AccessFlags {
	maskOwner: boolean;
	hideProductHistory: boolean;
}

const defaultFlags: AccessFlags = { maskOwner: false, hideProductHistory: false };

/**
 * 取得當前廠區的 access flags
 * 從 URL hash 中提取 plant ID，對照 maskedCustomers 映射
 */
export function getAccessFlags(): AccessFlags {
	try {
		const raw = sessionStorage.getItem("accessFlags");
		if (!raw) return defaultFlags;
		const data = JSON.parse(raw);

		// 新格式：per-customer mapping
		if (data.maskedCustomers) {
			const plantId = getCurrentPlantId();
			if (plantId && data.maskedCustomers[plantId]) {
				return { ...defaultFlags, ...data.maskedCustomers[plantId] };
			}
			return defaultFlags;
		}

		// 舊格式相容（全域）
		return { ...defaultFlags, ...data };
	} catch {
		return defaultFlags;
	}
}

/**
 * 從 URL hash 提取當前 plant ID
 * 格式: #/:lng/plant/:plantId/...
 */
function getCurrentPlantId(): string | null {
	try {
		const hash = window.location.hash || "";
		const match = hash.match(/\/plant\/([a-f0-9]{24})/);
		return match ? match[1] : null;
	} catch {
		return null;
	}
}

/**
 * 取得指定 customer 的 access flags（不依賴 URL）
 */
export function getAccessFlagsForCustomer(customerId: string): AccessFlags {
	try {
		const raw = sessionStorage.getItem("accessFlags");
		if (!raw) return defaultFlags;
		const data = JSON.parse(raw);
		if (data.maskedCustomers?.[customerId]) {
			return { ...defaultFlags, ...data.maskedCustomers[customerId] };
		}
		return defaultFlags;
	} catch {
		return defaultFlags;
	}
}

// flags 是登入後才非同步抓回來存進 sessionStorage 的，sessionStorage 不會通知畫面——
// 比它早畫好的頁面（多廠帳號登入後第一站的地圖）會漏遮。寫入一律走 storeAccessFlags，
// 需要的頁面呼叫 useAccessFlagsVersion，flags 到位後重畫（2026-10-15）。
const ACCESS_FLAGS_EVENT = "accessflagschange";

export function storeAccessFlags(flags: unknown): void {
	sessionStorage.setItem("accessFlags", JSON.stringify(flags));
	window.dispatchEvent(new Event(ACCESS_FLAGS_EVENT));
}

export function useAccessFlagsVersion(): number {
	const [version, setVersion] = useState(0);
	useEffect(() => {
		const bump = () => setVersion((v) => v + 1);
		window.addEventListener(ACCESS_FLAGS_EVENT, bump);
		return () => window.removeEventListener(ACCESS_FLAGS_EVENT, bump);
	}, []);
	return version;
}

/**
 * 遮蔽文字（如公司名、廠區名）
 * "福陽企業股份有限公司" → "＊＊＊＊公司"
 */
export function maskText(text: string): string {
	if (!text) return text;
	if (text.length <= 2) return "＊＊";
	const keep = 2;
	const masked = "＊".repeat(Math.min(text.length - keep, 4));
	return masked + text.slice(-keep);
}
